import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionItem,
  Input,
  Button,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  Checkbox,
  Select,
  SelectItem,
  Badge,
  addToast
} from "@heroui/react";
import {
  AdjustmentsVerticalIcon,
  FunnelIcon,
  XMarkIcon
} from "@heroicons/react/24/outline";
import { NFTCollectionsFilterList } from "../../api/methods-marketplace";
import NftCard from "../../components/nft-card";
import { useNavigate, useSearchParams } from "react-router-dom";
import NftCardLoader from "../nft-card/nft-card-loader";

export const currencyOptions = [
  { key: "usd", label: "US Dollar (USD)" },
  { key: "baf", label: "BAF Token" }
];

const ExploreMarketplaceComponent = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [nftTypesValue, setNftTypesValue] = useState("");
  const [assetTypesValue, setAssetTypesValue] = useState("");
  const [filterCount, setFilterCount] = useState(0);
  const [sortOption, setSortOption] = useState("");

  const [searchParams] = useSearchParams();
  const buttonDisabled =
    Number(minPrice) >= Number(maxPrice) ||
    minPrice === "" ||
    maxPrice === "" ||
    Number(minPrice) <= 0 ||
    Number(maxPrice) <= 0;

  const navigate = useNavigate();

  const handleOnClick = () => {
    setIsOpen(!isOpen);
  };

  const [cardCollection, setCardCollection] = useState([]);

  // Client-side filtering function
  const applyClientSideFilters = (data) => {
    let filteredData = [...data];

    // Filter by price range
    const minPriceFilter = searchParams.get("min_price");
    const maxPriceFilter = searchParams.get("max_price");
    if (minPriceFilter && maxPriceFilter) {
      const min = parseFloat(minPriceFilter);
      const max = parseFloat(maxPriceFilter);
      filteredData = filteredData.filter((item) => {
        const price = parseFloat(
          item?.instant_sale_price || item?.resale_price || 0
        );
        return price >= min && price <= max;
      });
    }

    // Filter by NFT type (tradable/non_tradable)
    const nftType = searchParams.get("nft_type");
    if (nftType) {
      filteredData = filteredData.filter((item) => {
        const contractType = item?.nft_contract?.contract_type;
        return contractType === nftType;
      });
    }

    // Filter by Asset type (digital/physical)
    const assetType = searchParams.get("asset_type");
    if (assetType) {
      filteredData = filteredData.filter((item) => {
        if (assetType === "physical") {
          return item?.physical_nft === true;
        } else if (assetType === "digital") {
          return item?.physical_nft === false;
        }
        return true;
      });
    }

    // Apply sorting
    const sort = searchParams.get("sort");
    if (sort) {
      filteredData = [...filteredData].sort((a, b) => {
        const priceA = parseFloat(
          a?.instant_sale_price || a?.resale_price || 0
        );
        const priceB = parseFloat(
          b?.instant_sale_price || b?.resale_price || 0
        );

        switch (sort) {
          case "price_low_to_high":
            return priceA - priceB;
          case "price_high_to_low":
            return priceB - priceA;
          case "recently_listed":
            return new Date(b.created_at) - new Date(a.created_at);
          default:
            return 0;
        }
      });
    }

    return filteredData;
  };

  const handleCardCollection = async (queryString = "") => {
    setIsLoading(true);

    try {
      const result = await NFTCollectionsFilterList(queryString);

      if (result?.data?.data) {
        // First filter: Remove items without any price
        let filteredData = result.data.data.filter(
          (item) =>
            (item?.resale_price !== null && item?.resale_price !== undefined) ||
            (item?.instant_sale_price !== null &&
              item?.instant_sale_price !== undefined)
        );

        // Apply client-side filters for price, nft_type, asset_type, and sorting
        filteredData = applyClientSideFilters(filteredData);

        setCardCollection(filteredData);
      }
    } catch (err) {
      console.log("🚀 ~ handleCardCollection ~ err:", err);
    } finally {
      setIsLoading(false);
    }
  };

  // Initialize filters from URL params
  useEffect(() => {
    const min_price = searchParams.get("min_price");
    const max_price = searchParams.get("max_price");
    const nft_type = searchParams.get("nft_type");
    const asset_type = searchParams.get("asset_type");
    const sort = searchParams.get("sort");

    setMinPrice(min_price || "");
    setMaxPrice(max_price || "");
    setNftTypesValue(nft_type || "");
    setAssetTypesValue(asset_type || "");
    setSortOption(sort || "");

    // Count active filters
    let count = 0;
    if (min_price && max_price) count++;
    if (nft_type) count++;
    if (asset_type) count++;
    setFilterCount(count);

    handleCardCollection(searchParams.toString());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleOnchange = (event, section) => {
    const newSearchParams = new URLSearchParams(searchParams);
    const name = event?.target?.name;
    const value = event?.target?.value;

    switch (section) {
      case "nft_type":
        setNftTypesValue(value);
        if (value) {
          newSearchParams.set("nft_type", value);
        } else {
          newSearchParams.delete("nft_type");
        }
        break;

      case "asset_type":
        setAssetTypesValue(value);
        if (value) {
          newSearchParams.set("asset_type", value);
        } else {
          newSearchParams.delete("asset_type");
        }
        break;
      default:
        break;
    }

    const queryString = newSearchParams.toString();
    navigate(`?${queryString}`);
  };

  const handleSortChange = (key) => {
    const newSearchParams = new URLSearchParams(searchParams);
    setSortOption(key);

    if (key) {
      newSearchParams.set("sort", key);
    } else {
      newSearchParams.delete("sort");
    }

    const queryString = newSearchParams.toString();
    navigate(`?${queryString}`);
  };

  const handleApplyPriceFilter = () => {
    const newSearchParams = new URLSearchParams(searchParams);
    const minPriceNum = parseFloat(minPrice);
    const maxPriceNum = parseFloat(maxPrice);

    if (!minPrice || !maxPrice) {
      addToast({
        title: "Invalid Price Range",
        description: "Both min and max price should be provided.",
        color: "danger",
        variant: "bordered"
      });
      return;
    }

    if (minPriceNum <= 0 || maxPriceNum <= 0) {
      addToast({
        title: "Invalid Price Range",
        description: "Prices must be greater than 0.",
        color: "danger",
        variant: "bordered"
      });
      return;
    }

    if (minPriceNum >= maxPriceNum) {
      addToast({
        title: "Invalid Price Range",
        description: "Min price must be less than max price.",
        color: "danger",
        variant: "bordered"
      });
      return;
    }

    newSearchParams.set("min_price", minPrice);
    newSearchParams.set("max_price", maxPrice);

    const queryString = newSearchParams.toString();
    navigate(`?${queryString}`);
  };

  const handleClearPriceFilter = () => {
    setMinPrice("");
    setMaxPrice("");

    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete("min_price");
    newSearchParams.delete("max_price");

    const queryString = newSearchParams.toString();
    navigate(`?${queryString}`);
  };

  const handlePriceInputChange = (event) => {
    const { name, value } = event.target;

    // Allow decimal numbers with up to 2 decimal places
    if (/^\d*\.?\d{0,2}$/.test(value)) {
      if (name === "min_price") {
        setMinPrice(value);
      } else if (name === "max_price") {
        setMaxPrice(value);
      }
    }
  };

  const handleClearAllFilters = () => {
    setMinPrice("");
    setMaxPrice("");
    setNftTypesValue("");
    setAssetTypesValue("");
    setSortOption("");

    navigate("?");
  };

  const NftTypes = [
    { key: "tradable", label: "Tradable" },
    { key: "non_tradable", label: "Non Tradable" }
  ];

  const AssetTypes = [
    { key: "digital", label: "Digital" },
    { key: "physical", label: "Physical" }
  ];

  const sortOptions = [
    { key: "price_low_to_high", label: "Price low to high" },
    { key: "price_high_to_low", label: "Price high to low" },
    { key: "recently_listed", label: "Recently Listed" }
  ];

  return (
    <>
      <div className="px-4 max-w-7xl mx-auto py-12 min-h-screen">
        <div className="flex justify-between mt-4">
          {filterCount > 0 ? (
            <Badge
              color="success"
              content={filterCount}
              placement="top-left"
              shape="circle"
              classNames={{
                badge: "bg-yellowbuy border-black"
              }}
            >
              <Button
                className="bg-black text-white"
                startContent={<FunnelIcon className="w-4 h-4" />}
                size="sm"
                radius="full"
                onPress={handleOnClick}
              >
                Filter
              </Button>
            </Badge>
          ) : (
            <Button
              className="bg-black text-white"
              startContent={<FunnelIcon className="w-4 h-4" />}
              size="sm"
              radius="full"
              onPress={handleOnClick}
            >
              Filter
            </Button>
          )}

          <Dropdown placement="bottom-end">
            <DropdownTrigger>
              <Button
                className="bg-black text-white"
                startContent={<AdjustmentsVerticalIcon className="w-4 h-4" />}
                size="sm"
                radius="full"
              >
                Sort
              </Button>
            </DropdownTrigger>
            <DropdownMenu
              aria-label="sort Actions"
              variant="flat"
              selectedKeys={sortOption ? [sortOption] : []}
              selectionMode="single"
              onSelectionChange={(keys) => {
                const selected = Array.from(keys)[0];
                handleSortChange(selected);
              }}
            >
              {sortOptions.map((option) => (
                <DropdownItem key={option.key}>{option.label}</DropdownItem>
              ))}
            </DropdownMenu>
          </Dropdown>
        </div>
        <div className="flex gap-2 my-6">
          <div
            className={`flex flex-col bg-[#E9E9E9] overflow-y-auto ${
              isOpen
                ? "py-3 px-1 fixed inset-0 w-full h-dvh z-[2000] lg:static lg:z-[0] lg:h-[30rem] lg:w-[17rem] lg:rounded-xl"
                : "hidden"
            }`}
          >
            <div className="p-4 flex justify-between items-center gap-4">
              <h6 className="m-0 text-xl text-black font-semibold">Filter</h6>
              <Button
                color="primary"
                size="sm"
                className="bg-yellowbuy text-black border border-black hidden lg:block disabled:opacity-50 disabled:bg-[#D3D3D3]"
                radius="sm"
                disabled={!filterCount}
                onPress={handleClearAllFilters}
              >
                Clear All
              </Button>
              <XMarkIcon
                className="w-6 h-6 text-black cursor-pointer lg:hidden"
                onClick={() => setIsOpen(false)}
              />
            </div>
            <Accordion variant="splitted" selectionMode="multiple">
              <AccordionItem
                key="1"
                aria-label="price"
                title="Price"
                classNames={{
                  base: "shadow-none",
                  indicator: "-rotate-90 data-[open=true]:rotate-90"
                }}
              >
                <div className="flex items-center gap-3">
                  <Input
                    label=""
                    labelPlacement="outside"
                    placeholder="Min"
                    className="flex-1"
                    type="text"
                    name="min_price"
                    value={minPrice}
                    onChange={handlePriceInputChange}
                  />
                  <p className="m-0 text-[#6A6A6A] text-base">to</p>
                  <Input
                    label=""
                    labelPlacement="outside"
                    placeholder="Max"
                    className="flex-1"
                    type="text"
                    name="max_price"
                    value={maxPrice}
                    onChange={handlePriceInputChange}
                  />
                </div>
                <div className="flex items-center gap-3 mt-3">
                  <Button
                    className="flex-1 bg-black text-white border border-black h-8"
                    radius="sm"
                    onPress={handleClearPriceFilter}
                  >
                    Clear
                  </Button>
                  <Button
                    className={`flex-1 ${
                      buttonDisabled ? "bg-[#C0C0C0]" : "bg-yellowbuy"
                    }  text-dark border border-black h-8`}
                    radius="sm"
                    disabled={buttonDisabled}
                    onPress={handleApplyPriceFilter}
                  >
                    Apply
                  </Button>
                </div>
              </AccordionItem>

              <AccordionItem
                key="3"
                aria-label="NFT Types"
                title="NFT Types"
                classNames={{
                  base: "shadow-none",
                  indicator: "-rotate-90 data-[open=true]:rotate-90"
                }}
              >
                <Select
                  className="max-w-xs"
                  label="Nft type"
                  labelPlacement="outside"
                  placeholder="select nft type"
                  classNames={{
                    base: "data-[has-label=true]:mt-0",
                    label: "hidden"
                  }}
                  selectedKeys={nftTypesValue ? [nftTypesValue] : []}
                  name="nft_type"
                  onChange={(event) => handleOnchange(event, "nft_type")}
                >
                  {NftTypes.map((type) => (
                    <SelectItem key={type.key}>{type.label}</SelectItem>
                  ))}
                </Select>
              </AccordionItem>
              <AccordionItem
                key="4"
                aria-label="Asset Types"
                title="Asset Types"
                classNames={{
                  base: "shadow-none",
                  indicator: "-rotate-90 data-[open=true]:rotate-90"
                }}
              >
                <Select
                  className="max-w-xs"
                  label="Asset type"
                  labelPlacement="outside"
                  placeholder="select asset type"
                  classNames={{
                    base: "data-[has-label=true]:mt-0",
                    label: "hidden"
                  }}
                  selectedKeys={assetTypesValue ? [assetTypesValue] : []}
                  name="asset_type"
                  onChange={(event) => handleOnchange(event, "asset_type")}
                >
                  {AssetTypes.map((type) => (
                    <SelectItem key={type.key}>{type.label}</SelectItem>
                  ))}
                </Select>
              </AccordionItem>
            </Accordion>
            <div className="px-2 mt-4">
              <Button
                color="primary"
                size="sm"
                className="bg-[#000000] text-white w-full border border-black lg:hidden disabled:opacity-50 disabled:bg-[#D3D3D3]"
                radius="sm"
                disabled={!filterCount}
                onPress={handleClearAllFilters}
              >
                Clear All
              </Button>
            </div>
          </div>
          <div
            className={`grid grid-cols-1 sm:grid-cols-2 gap-4 ${
              isOpen
                ? "lg:w-[calc(100%-17.5rem)] lg:grid-cols-2 xl:grid-cols-3"
                : "w-full lg:grid-cols-3 xl:grid-cols-4"
            }`}
          >
            {isLoading ? (
              [...Array(12)].map((_, index) => <NftCardLoader key={index} />)
            ) : cardCollection.length > 0 ? (
              cardCollection.map((collection, index) => (
                <NftCard key={index} data={collection} />
              ))
            ) : filterCount > 0 ? (
              <div className="col-span-full flex flex-col items-center justify-center py-12 text-center">
                <div className="text-lg font-medium text-gray-600">
                  No matching NFTs found.
                </div>
                <div className="text-sm text-gray-500 mt-1">
                  Modify your filters or browse the full collection.
                </div>
              </div>
            ) : (
              <div className="col-span-full flex flex-col items-center justify-center py-12 text-center">
                <div className="text-lg font-medium text-gray-600">
                  OOPS! No NFTs found.
                </div>
                <div className="text-sm text-gray-500 mt-1">
                  There are no NFTs available in the marketplace at the moment.
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ExploreMarketplaceComponent;
